import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Card, CardContent, Chip, Grid,
  CircularProgress, Alert, Button, Divider, Avatar
} from '@mui/material';
import {
  MenuBook, Assignment, Folder, AttachFile, VideoLibrary,
  PlayCircle, Download
} from '@mui/icons-material';
import { useAuth } from '../../store/authStore';
import { getStudentLessonPosts } from '../../api/lessonPosts';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../api/config';

const TYPE_CONFIG = {
  lesson: { label: 'Lesson', icon: <MenuBook />, color: '#F5A623' },
  exercise: { label: 'Exercise', icon: <Assignment />, color: '#4CAF50' },
  resource: { label: 'Resource', icon: <Folder />, color: '#2196F3' },
};

const MyLessons = () => {
  const { user } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teacherNames, setTeacherNames] = useState({});
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadLessons();
  }, [user?.id]);

  const loadLessons = async () => {
    if (!user?.id) return;
    try {
      const data = await getStudentLessonPosts(user.id);
      setLessons(data);

      // Load teacher names
      const uniqueTeacherIds = [...new Set(data.map(l => l.teacherId))];
      const names = {};
      for (const tid of uniqueTeacherIds) {
        try {
          const teacherDoc = await getDoc(doc(db, 'users', tid));
          if (teacherDoc.exists()) {
            names[tid] = teacherDoc.data().displayName || 'Teacher';
          }
        } catch (e) { /* skip */ }
      }
      setTeacherNames(names);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const filtered = filter === 'all' ? lessons : lessons.filter(l => l.type === filter);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#F5A623' }} /></Box>;
  }

  return (
    <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', pt: 10, pb: 4 }}>
      <Box sx={{ maxWidth: 900, mx: 'auto', px: 2 }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>My Lessons</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Lessons, exercises, and resources from your teachers
        </Typography>

        {/* Filter Chips */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          {[
            { id: 'all', label: 'All' },
            { id: 'lesson', label: 'Lessons' },
            { id: 'exercise', label: 'Exercises' },
            { id: 'resource', label: 'Resources' },
          ].map(f => (
            <Chip
              key={f.id} label={f.label}
              onClick={() => setFilter(f.id)}
              sx={{
                bgcolor: filter === f.id ? '#F5A623' : '#fff',
                color: filter === f.id ? '#fff' : '#666',
                fontWeight: 600, border: '1px solid #eee',
                '&:hover': { bgcolor: filter === f.id ? '#e09500' : '#f5f5f5' }
              }}
            />
          ))}
        </Box>

        {filtered.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <MenuBook sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {lessons.length === 0 ? 'No lessons available' : 'No matching lessons'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {lessons.length === 0
                ? 'Book lessons with teachers to access their course materials'
                : 'Try a different filter'}
            </Typography>
          </Paper>
        ) : (
          filtered.map(lesson => {
            const config = TYPE_CONFIG[lesson.type] || TYPE_CONFIG.lesson;
            const isExpanded = expandedLesson === lesson.id;
            return (
              <Card key={lesson.id} sx={{
                mb: 2, border: '1px solid #eee',
                borderLeft: `4px solid ${config.color}`,
                transition: 'all 0.2s',
                '&:hover': { boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Chip
                          icon={config.icon}
                          label={config.label}
                          size="small"
                          sx={{ bgcolor: `${config.color}15`, color: config.color, fontWeight: 600 }}
                        />
                        {lesson.language && (
                          <Chip label={lesson.language} size="small" variant="outlined" />
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(lesson.createdAt)}
                        </Typography>
                      </Box>

                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        {lesson.title}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: '#F5A623' }}>
                          {(teacherNames[lesson.teacherId] || 'T').charAt(0)}
                        </Avatar>
                        <Typography variant="body2" color="text.secondary">
                          {teacherNames[lesson.teacherId] || 'Teacher'}
                        </Typography>
                      </Box>

                      <Typography variant="body2" color="text.secondary">
                        {lesson.description}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Expand/Collapse */}
                  {(lesson.content || lesson.attachments?.length > 0 || lesson.videoUrl) && (
                    <Button
                      size="small"
                      onClick={() => setExpandedLesson(isExpanded ? null : lesson.id)}
                      sx={{ mt: 1, color: '#F5A623', fontWeight: 600 }}
                    >
                      {isExpanded ? 'Show less' : 'Read more'}
                    </Button>
                  )}

                  {isExpanded && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                      {lesson.content && (
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mb: 2 }}>
                          {lesson.content}
                        </Typography>
                      )}

                      {lesson.videoUrl && (
                        <Button
                          variant="outlined" startIcon={<PlayCircle />}
                          href={lesson.videoUrl} target="_blank"
                          sx={{ mb: 2, borderColor: '#F5A623', color: '#F5A623' }}
                        >
                          Watch Video
                        </Button>
                      )}

                      {lesson.attachments?.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" gutterBottom>Attachments</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {lesson.attachments.map((a, i) => (
                              <Chip
                                key={i}
                                icon={<Download />}
                                label={a.name}
                                component="a"
                                href={a.url}
                                target="_blank"
                                clickable
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default MyLessons;
